import { Tooltip } from '@mantine/core';
import type { RunStatus, TestRunStatusHistory } from '@prisma/client';
import {
  IconCircleCheck,
  IconCircleCheckFilled,
  IconCircleX,
  IconCircleXFilled,
  IconStrikethrough,
  IconDotsCircleHorizontal,
} from '@tabler/icons-react';
import { match } from 'ts-pattern';
import { capFirst } from '~/common/string/capitalize';
import IconCircleLoadingAnimated from './IconCircleLoadingAnimated';

const DEFAULT_ICON_SIZE = 20;

const TEST_RUN_STATUS_TAILWIND_COLORS: Record<RunStatus, string> = {
  PASSED: 'text-green-600',
  FLAKY: 'text-yellow-400',
  FAILED: 'text-red-500',
  RUNNING: 'text-blue-400',
  RETRYING: 'text-blue-400',
  ERROR: 'text-yellow-500',
  SKIPPED: 'text-gray-500',
  QUEUED: 'text-gray-500',
  CANCELLED: 'text-gray-500',
};

export default function StatusIcon({
  status,
  statusHistory = [],
  size = DEFAULT_ICON_SIZE,
  filled,
  showTooltip = true,
}: {
  status: RunStatus;
  statusHistory?: TestRunStatusHistory[];
  size?: number;
  filled?: boolean;
  showTooltip?: boolean;
}) {
  const IconCircleCheckDynamic = filled ? IconCircleCheckFilled : IconCircleCheck;
  const IconCircleXDynamic = filled ? IconCircleXFilled : IconCircleX;

  const tooltipText =
    showTooltip ?
      statusHistory.length > 0 ?
        `Override: ${statusHistory[0].status} → ${status}${statusHistory.at(-1)?.note ? ` (${statusHistory.at(-1)?.note})` : ''}`
      : capFirst(status.toLowerCase())
    : '';

  const statusTailwindColor = TEST_RUN_STATUS_TAILWIND_COLORS[status];

  const iconElement = match(status)
    .with('RUNNING', 'RETRYING', () => (
      <div className="w-fit">
        <IconCircleLoadingAnimated
          color="#3B82F6"
          size={size}
        />
      </div>
    ))
    .with('PASSED', 'FLAKY', () => (
      <IconCircleCheckDynamic
        className={`flex-shrink-0 ${statusTailwindColor}`}
        size={size}
      />
    ))
    .with('SKIPPED', () => (
      <IconStrikethrough
        className={`flex-shrink-0 ${statusTailwindColor}`}
        size={size}
      />
    ))
    .with('FAILED', () => (
      <IconCircleXDynamic
        className={`flex-shrink-0 ${statusTailwindColor}`}
        size={size}
      />
    ))
    .with('QUEUED', () => (
      <IconDotsCircleHorizontal
        className={`flex-shrink-0 ${statusTailwindColor}`}
        size={size}
      />
    ))
    .with('ERROR', 'CANCELLED', () => (
      <IconCircleCheck
        className={`flex-shrink-0 ${statusTailwindColor}`}
        size={size}
      />
    ))
    .exhaustive();

  return (
    <Tooltip
      disabled={!showTooltip}
      label={tooltipText}
    >
      {iconElement}
    </Tooltip>
  );
}
